import dayjs from 'dayjs';
import { useRouter } from 'next/router';
import { ReactNode, useState } from 'react';

// Hooks
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

// Lib
import { LinkHrefType } from '@/lib/menu/main';
import NEW_FEATURES_RELEASED from '@/lib/releases/newFeaturesReleased';

// Components
import { GLOBAL_EVENT_QUERY } from '@/components/composite/Listeners/GlobalEventsListener';

// Utils
import {
    getLocalStorageNewFeatureNotification,
    setLocalStorageNewFeatureNotification
} from '@/utils/localStorage/newFeatureNotificationLocalStorage';

// Types
import type { NewFeatureReleasedType } from '@/lib/releases/newFeaturesReleased';

const NOTIFICATION_DURATION = 60;

export const useNewFeatureNotifications = () => {
    const { activeSocialProfile } = useActiveSocialProfile();

    const [newFeaturesVisited, setNewFeaturesVisited] = useState(
        getLocalStorageNewFeatureNotification()
    );

    const markNewFeatureAsVisited = (feature: NewFeatureReleasedType['name']) => {
        setLocalStorageNewFeatureNotification([...newFeaturesVisited, feature]);
        setNewFeaturesVisited((prevState: NewFeatureReleasedType['name'][]) => [
            ...prevState,
            feature
        ]);
    };

    const isNewFeatureOnPages = (urls: LinkHrefType[]) => {
        let hasNewFeature = null;
        if (urls) {
            urls.forEach((url) => {
                NEW_FEATURES_RELEASED.forEach((feature) => {
                    if (
                        feature?.name &&
                        !newFeaturesVisited.includes(feature?.name) &&
                        ((!feature?.urls && !feature?.globalEventQuery) ||
                            (typeof url !== 'object' && feature?.urls?.includes(url)) ||
                            (typeof url === 'object' &&
                                feature?.globalEventQuery &&
                                url?.query?.[GLOBAL_EVENT_QUERY] === feature?.globalEventQuery)) &&
                        (!feature?.socialNetworkType ||
                            feature?.socialNetworkType.includes(
                                activeSocialProfile?.social_network_type
                            ))
                    ) {
                        if (feature?.date) {
                            const dateDiff = dayjs().diff(
                                dayjs(feature?.date).format('YYYY-MM-DD'),
                                'day'
                            );
                            if (dateDiff >= 0 && dateDiff <= NOTIFICATION_DURATION) {
                                hasNewFeature = feature?.name;
                            }
                        } else {
                            hasNewFeature = feature?.name;
                        }
                    }
                });
            });
        }

        return hasNewFeature;
    };

    return {
        newFeaturesVisited,
        markNewFeatureAsVisited,
        isNewFeatureOnPages
    };
};

const PageNewFeatureVisitedHandler = ({ children }: { children: ReactNode }) => {
    const { pathname } = useRouter();
    const urlWithoutParams = pathname.split('?')[0];

    const { markNewFeatureAsVisited, isNewFeatureOnPages } = useNewFeatureNotifications();

    const currentNewFeature = isNewFeatureOnPages([urlWithoutParams]);

    if (currentNewFeature) {
        markNewFeatureAsVisited(currentNewFeature);
    }

    return children;
};

export default PageNewFeatureVisitedHandler;
